// src/pages/JDMatching.jsx
import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabaseClient';
import { Target, CheckCircle, XCircle, AlertCircle, TrendingUp } from 'lucide-react';

const JDMatching = () => {
  const { user, loading: authLoading } = useAuth(); // ✅ Get auth loading state
  const [resume, setResume] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  // ✅ Show loading while auth is checking
  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  const handleAnalyze = async () => {
    // Validation
    if (!user) {
      setError('Please login first.');
      return;
    }

    if (!resume.trim() || !jobDescription.trim()) {
      setError('Please provide both resume and job description.');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysis(null);

    try {
      console.log('🔍 Starting ATS analysis...');
      
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ Session error:', sessionError);
        throw new Error('Please login again.');
      }
      
      if (!session) {
        throw new Error('No active session. Please login.');
      }

      console.log('✅ User authenticated, calling Edge Function...');
      console.log('📝 Resume length:', resume.length);
      console.log('📝 Job Description length:', jobDescription.length);

      const response = await fetch(
        'https://tafawwqhjtzreeirjsql.supabase.co/functions/v1/ats-job-matching',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            resume: resume.trim(),
            jobDescription: jobDescription.trim()
          }),
        }
      );

      console.log('📊 Response status:', response.status);

      const data = await response.json();
      console.log('📊 Response data:', data);

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      if (!data.analysis) {
        throw new Error('No analysis data received from the server.');
      }

      console.log('✅ Analysis complete!');
      setAnalysis(data.analysis);
      
    } catch (err) {
      console.error('❌ Analysis failed:', err);
      setError(err.message || 'Failed to analyze. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-4" style={{ paddingTop: '80px' }}>
      <Container>
       <div className="d-flex align-items-center gap-3 mb-4">
       <Target size={32} style={{ color: '#1A9E96', stroke: '#1A9E96' }} />
       <h1 className="display-6 fw-bold">ATS Job Matching</h1>
  <    span className="badge-active-header"></span>
       </div>

        <p className="text-muted mb-4">
          Paste your resume and a job description to get an ATS match score, identify missing skills, and get improvement recommendations.
        </p>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {!user && !authLoading && (
          <Alert variant="warning">
            <strong>Please login</strong> to use the ATS Job Matching feature.
          </Alert>
        )}

        <Row className="g-4">
          <Col lg={6}>
            <Card className="shadow-sm border-0">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Your Resume</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={8}
                    value={resume}
                    onChange={(e) => setResume(e.target.value)}
                    placeholder="Paste your resume text here..."
                    disabled={loading || !user}
                    className="font-monospace"
                  />
                  <Form.Text className="text-muted">
                    {resume.length} characters
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Job Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the job description here..."
                    disabled={loading || !user}
                    className="font-monospace"
                  />
                  <Form.Text className="text-muted">
                    {jobDescription.length} characters
                  </Form.Text>
                </Form.Group>

                <Button 
                  onClick={handleAnalyze}
                  disabled={loading || !resume.trim() || !jobDescription.trim() || !user}
                  className="w-100 gap-2 d-flex align-items-center justify-content-center"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" animation="border" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <TrendingUp size={18} />
                      Analyze Match
                    </>
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <h5 className="fw-bold mb-3">Analysis Results</h5>
                {loading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ height: '400px' }}>
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Analyzing your resume against the job description...</p>
                    <small>This may take a few seconds</small>
                  </div>
                ) : analysis ? (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {/* Match Score */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold">Match Score</span>
                        <span className="fw-bold" style={{ fontSize: '1.5rem' }}>
                          {analysis.matchScore || 0}%
                        </span>
                      </div>
                      <ProgressBar 
                        now={analysis.matchScore || 0} 
                        variant={analysis.matchScore >= 70 ? 'success' : analysis.matchScore >= 50 ? 'warning' : 'danger'}
                        style={{ height: '12px', borderRadius: '6px' }}
                      />
                    </div>

                    {/* Matching Skills */}
                    {analysis.matchingSkills?.length > 0 && (
                      <div className="mb-3">
                        <h6 className="fw-bold text-success">
                          <CheckCircle size={16} className="me-2" />
                          Matching Skills ({analysis.matchingSkills.length})
                        </h6>
                        <div className="d-flex flex-wrap gap-1">
                          {analysis.matchingSkills.map((skill, i) => (
                            <span key={i} className="badge bg-success">{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Missing Skills */}
                    {analysis.missingSkills?.length > 0 && (
                      <div className="mb-3">
                        <h6 className="fw-bold text-danger">
                          <XCircle size={16} className="me-2" />
                          Missing Skills ({analysis.missingSkills.length})
                        </h6>
                        <div className="d-flex flex-wrap gap-1">
                          {analysis.missingSkills.map((skill, i) => (
                            <span key={i} className="badge bg-danger">{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {analysis.recommendations && (
                      <div className="mb-3">
                        <h6 className="fw-bold text-primary">
                          <AlertCircle size={16} className="me-2" />
                          Recommendations
                        </h6>
                        <p className="small mb-0">{analysis.recommendations}</p>
                      </div>
                    )}

                    {/* Summary */}
                    {analysis.summary && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <h6 className="fw-bold">Summary</h6>
                        <p className="small mb-0">{analysis.summary}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ height: '400px' }}>
                    <Target size={48} className="mb-3 opacity-25" />
                    <p>Analysis results will appear here</p>
                    <small className="text-muted">Paste resume and job description, then click analyze</small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default JDMatching;